import React, { useEffect, useMemo } from 'react'
import Box from '../../components/box'
import Button from '../../components/button'
import TextField from '../../components/textfield'
import Dialog, { DialogActions, DialogContent, DialogTitle } from '../../components/dialog'
import { useOnInputChange } from '../../hooks/useOnInputChange'
import Typography from '../../components/typography'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { UserService } from '../../apilibrary'
import useCurrentUserId from '../../hooks/useCurrentUserId'
import { trackValidSpotifyLink } from '../../utils/analytics'

interface HomeFragmentProps {
  userId: string
}

const HomeFragment: React.FC<HomeFragmentProps> = ({
  userId: profileUserId
}) => {
  const [openSpotifyLinkModal, setOpenSpotifyLinkModal] = React.useState<boolean>(false)
  const [spotifyPlaylistId, setSpotifyPlaylistId] = React.useState<string>()
  const [spotifyLinkFormInput, handleChangeSpotifyLink] = useOnInputChange('')
  const [dialogErrorMessage, setDialogErrorMessage] = React.useState<string>()
  const currentUserId = useCurrentUserId()
  const {
    runFunction: saveSpotifyPlaylistId,
    loading: loadingSaveSpotifyPlaylistId
  } = useAsyncOperation(async (playlist: string) => {
    if (!currentUserId) {
      throw new Error('User is not logged in')
    }
    await UserService.setUserProfileSpotifyPlaylistId(currentUserId, playlist);
  })
  const {
    runFunction: getUserProfileSpotifyPlaylistId
    // loading: loadingGetUserProfileSpotifyPlaylistId
  } = useAsyncOperation(UserService.getUserProfileSpotifyPlaylistId)

  useEffect(() => {
    const getSpotifyPlaylistId = async (): Promise<void> => {
      const playlistId = await getUserProfileSpotifyPlaylistId(profileUserId)
      setSpotifyPlaylistId(playlistId)
      if (playlistId) {
        await trackValidSpotifyLink(playlistId, profileUserId)
        setSpotifyPlaylistId(playlistId)
      } else {
        setSpotifyPlaylistId(undefined)
      }
    }
    void getSpotifyPlaylistId()
  }, [getUserProfileSpotifyPlaylistId, profileUserId])

  const spotifyLinkModal = useMemo(() => {
    const parseSpotifyPlaylistId = (spotifyLink: string): string => {
      const spotifyPlaylistIdRegex = /https:\/\/open\.spotify\.com\/playlist\/([a-zA-Z0-9]+)/
      const match = spotifyLink.match(spotifyPlaylistIdRegex)
      console.log(match)
      if (match) {
        return match[1]
      }
      throw new Error('Invalid Spotify Playlist Link')
    }

    const handleSaveSpotifyLink = async (): Promise<void> => {
      try {
        const parsedSpotifyPlaylistId = parseSpotifyPlaylistId(spotifyLinkFormInput)
        console.log(parsedSpotifyPlaylistId)
        setSpotifyPlaylistId(parsedSpotifyPlaylistId)
        await saveSpotifyPlaylistId(parsedSpotifyPlaylistId)
        setOpenSpotifyLinkModal(false)
      } catch (error) {
        console.log(error)
        if (error instanceof Error) {
          setDialogErrorMessage(error.message)
        } else {
          setDialogErrorMessage('Something went wrong. Please try again later.')
        }
      }
    }

    return (
      <Dialog
        open={openSpotifyLinkModal}
        onClose={() => { setOpenSpotifyLinkModal(false); }}
      >
        <DialogTitle>
          Highlight your work by adding a Spotify playlist link
        </DialogTitle>
        <DialogContent>
          {dialogErrorMessage && (
            <Typography color={'error'} sx={{ mb: 2 }}>
              {dialogErrorMessage}
            </Typography>
          )}
          <TextField
            value={spotifyLinkFormInput}
            onChange={handleChangeSpotifyLink}
            fullWidth
            label='Spotify Playlist Link'
          />
        </DialogContent>
        <DialogActions>
          <Button
            disabled={loadingSaveSpotifyPlaylistId}
          onClick={handleSaveSpotifyLink}
            sx={{
              ml: 'auto'
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    )
  }, [openSpotifyLinkModal, dialogErrorMessage, spotifyLinkFormInput, handleChangeSpotifyLink, loadingSaveSpotifyPlaylistId, saveSpotifyPlaylistId])

  return (
    <Box>
      {spotifyLinkModal}
      {currentUserId === profileUserId && (
        <Button onClick={() => { setOpenSpotifyLinkModal(true); }} variant='outlined' sx={{ display: 'block', ml: 'auto', mb: 2 }}>
          Set Spotify Link
        </Button>
      )}
      {spotifyPlaylistId && (
        <iframe
        style={{
          borderRadius: '12px'
        }}
        src={`https://open.spotify.com/embed/playlist/${spotifyPlaylistId}?utm_source=generator&theme=0`}
        width="100%"
        height="352px"
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading="lazy" />
      )}
    </Box>
  )
}

export default HomeFragment
